/**
 * 🏢 Corporate Account Details Modal
 * View and manage corporate account details
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  TextField,
  IconButton,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  Close,
  Edit,
  Save,
  Cancel as CancelIcon,
  Business,
  Person,
  LocationOn,
  AccountBalance,
  History,
  CheckCircle,
  Error as ErrorIcon,
  Block,
  Delete,
  PlayArrow,
} from '@mui/icons-material';
import {
  CorporateAccount,
  corporateAccountsService,
} from '@/services/corporateAccountsService';
import { simplBookingService } from '@/services/bookingService';
import { SuspendCorporateAccountModal } from '@/components/corporate/SuspendCorporateAccountModal';
import { ResumeCorporateAccountModal } from '@/components/corporate/ResumeCorporateAccountModal';
import { DeleteCorporateAccountModal } from '@/components/corporate/DeleteCorporateAccountModal';
import toast from 'react-hot-toast';

interface CorporateAccountDetailsModalProps {
  open: boolean;
  account: CorporateAccount | null;
  onClose: () => void;
  onUpdate?: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString?: string) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'pending':
      return 'warning';
    case 'suspended':
      return 'error';
    case 'rejected':
      return 'default';
    default:
      return 'default';
  }
};

export const CorporateAccountDetailsModal: React.FC<CorporateAccountDetailsModalProps> = ({
  open,
  account,
  onClose,
  onUpdate,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [editingCreditLimit, setEditingCreditLimit] = useState(false);
  const [newCreditLimit, setNewCreditLimit] = useState('');
  const [creditLimitReason, setCreditLimitReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [bookings, setBookings] = useState<any[]>([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [bookingsError, setBookingsError] = useState<string | null>(null);

  // Fetch bookings when modal opens (for accurate statistics)
  useEffect(() => {
    const fetchBookings = async () => {
      if (account) {
        setLoadingBookings(true);
        setBookingsError(null);
        setBookings([]); // Reset bookings when account changes
        try {
          const corporateBookings = await simplBookingService.getCorporateAccountBookings(
            account.id,
            { limit: 100 }
          );
          // Sort bookings by createdAt descending (most recent first)
          const sortedBookings = corporateBookings.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0).getTime();
            const dateB = new Date(b.createdAt || 0).getTime();
            return dateB - dateA;
          });
          setBookings(sortedBookings);
        } catch (error) {
          console.error('Error fetching corporate bookings:', error);
          setBookingsError('Failed to load bookings');
        } finally {
          setLoadingBookings(false);
        }
      }
    };

    fetchBookings();
  }, [account]);

  if (!account) return null;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleEditCreditLimit = () => {
    setNewCreditLimit(account.creditLimit.toString());
    setCreditLimitReason('');
    setEditingCreditLimit(true);
  };

  const handleCancelEditCreditLimit = () => {
    setEditingCreditLimit(false);
    setNewCreditLimit('');
    setCreditLimitReason('');
  };

  const handleSaveCreditLimit = async () => {
    const newLimit = parseFloat(newCreditLimit);

    if (isNaN(newLimit) || newLimit < 0) {
      toast.error('Please enter a valid credit limit');
      return;
    }

    if (newLimit === account.creditLimit) {
      toast.error('Credit limit has not changed');
      return;
    }

    setSaving(true);
    try {
      const response = await corporateAccountsService.updateCreditLimit(account.id, {
        creditLimit: newLimit,
        reason: creditLimitReason || undefined,
      });

      if (response.success) {
        toast.success('Credit limit updated successfully');
        setEditingCreditLimit(false);
        if (onUpdate) onUpdate();
      } else {
        toast.error(response.error || 'Failed to update credit limit');
      }
    } catch (error) {
      console.error('Failed to update credit limit:', error);
      toast.error('Failed to update credit limit');
    } finally {
      setSaving(false);
    }
  };

  const renderOverviewTab = () => (
    <Grid container spacing={3}>
      {/* Company Information */}
      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Business sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Company Information</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Company Name
                </Typography>
                <Typography variant="body1">{account.companyName}</Typography>
              </Box>
              {account.registrationNumber && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Registration Number
                  </Typography>
                  <Typography variant="body1">{account.registrationNumber}</Typography>
                </Box>
              )}
              {account.taxId && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tax ID
                  </Typography>
                  <Typography variant="body1">{account.taxId}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={account.status.toUpperCase()}
                    color={getStatusColor(account.status)}
                    size="small"
                  />
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Contact Person */}
      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Person sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Contact Person</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Name
                </Typography>
                <Typography variant="body1">{account.contactPerson.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{account.contactPerson.email}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Phone
                </Typography>
                <Typography variant="body1">{account.contactPerson.phone}</Typography>
              </Box>
              {account.contactPerson.position && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Position
                  </Typography>
                  <Typography variant="body1">{account.contactPerson.position}</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Billing Address */}
      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOn sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Billing Address</Typography>
            </Box>
            <Typography variant="body1">
              {account.billingAddress.street}
              <br />
              {account.billingAddress.city}, {account.billingAddress.state}{' '}
              {account.billingAddress.zipCode}
              <br />
              {account.billingAddress.country}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Credit Information */}
      <Grid item xs={12} md={6}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Credit Information</Typography>
              </Box>
              {account.status === 'active' && !editingCreditLimit && (
                <IconButton size="small" onClick={handleEditCreditLimit}>
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </Box>

            {editingCreditLimit ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Credit Limit"
                  type="number"
                  value={newCreditLimit}
                  onChange={(e) => setNewCreditLimit(e.target.value)}
                  InputProps={{
                    startAdornment: '$',
                  }}
                />
                <TextField
                  fullWidth
                  label="Reason for Change (Optional)"
                  multiline
                  rows={2}
                  value={creditLimitReason}
                  onChange={(e) => setCreditLimitReason(e.target.value)}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={handleCancelEditCreditLimit}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleSaveCreditLimit}
                    disabled={saving}
                  >
                    Save
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Credit Limit
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(account.creditLimit)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current Balance
                  </Typography>
                  <Typography variant="body1" color="error.main">
                    {formatCurrency(account.currentBalance)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Available Credit
                  </Typography>
                  <Typography variant="body1" color="success.main" fontWeight="medium">
                    {formatCurrency(account.availableCredit)}
                  </Typography>
                </Box>
                {account.currentBalance > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Credit Utilization
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Box
                        sx={{
                          flexGrow: 1,
                          height: 8,
                          backgroundColor: 'grey.200',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${(account.currentBalance / account.creditLimit) * 100}%`,
                            height: '100%',
                            backgroundColor:
                              account.currentBalance / account.creditLimit > 0.9
                                ? 'error.main'
                                : account.currentBalance / account.creditLimit > 0.7
                                ? 'warning.main'
                                : 'success.main',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" fontWeight="medium">
                        {((account.currentBalance / account.creditLimit) * 100).toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Account Statistics */}
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Account Statistics
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Total Bookings
                  </Typography>
                  <Typography variant="h4">
                    {loadingBookings ? (
                      <CircularProgress size={20} />
                    ) : (
                      bookings.length
                    )}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last Booking
                  </Typography>
                  <Typography variant="body1">
                    {loadingBookings ? (
                      <CircularProgress size={16} />
                    ) : bookings.length > 0 && bookings[0]?.createdAt ? (
                      formatDate(bookings[0].createdAt)
                    ) : (
                      'N/A'
                    )}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Account Created
                  </Typography>
                  <Typography variant="body1">{formatDate(account.createdAt)}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body1">{formatDate(account.updatedAt)}</Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Approval/Rejection History */}
      {(account.approvedBy || account.rejectedBy) && (
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <History sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Approval History</Typography>
              </Box>
              {account.approvedBy && (
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
                  <CheckCircle color="success" fontSize="small" />
                  <Box>
                    <Typography variant="body2">
                      <strong>Approved</strong> by {account.approvedBy.name} ({account.approvedBy.email})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(account.approvedAt)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {account.rejectedBy && (
                <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                  <ErrorIcon color="error" fontSize="small" />
                  <Box>
                    <Typography variant="body2">
                      <strong>Rejected</strong> by {account.rejectedBy.name} ({account.rejectedBy.email})
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(account.rejectedAt)}
                    </Typography>
                    {account.rejectionReason && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Reason: {account.rejectionReason}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Notes */}
      {account.notes && (
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Notes
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {account.notes}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );

  const renderBookingsTab = () => {
    if (loadingBookings) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (bookingsError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {bookingsError}
        </Alert>
      );
    }

    if (bookings.length === 0) {
      return (
        <Box>
          <Typography variant="h6" gutterBottom>
            Bookings
          </Typography>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <History sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography color="text.secondary">
                No bookings found
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Bookings created for this corporate account will appear here
              </Typography>
            </CardContent>
          </Card>
        </Box>
      );
    }

    const getBookingStatusColor = (status: string) => {
      switch (status?.toLowerCase()) {
        case 'confirmed':
        case 'completed':
          return 'success';
        case 'pending':
          return 'warning';
        case 'cancelled':
          return 'error';
        case 'in_progress':
        case 'ongoing':
          return 'info';
        default:
          return 'default';
      }
    };

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Bookings ({bookings.length})
          </Typography>
        </Box>

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Booking Ref</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {bookings.map((booking) => (
                <TableRow key={booking.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {booking.bookingRef || booking.id?.substring(0, 8)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {booking.vehicle?.name || 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {booking.vehicle?.type || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {booking.companyName || account.companyName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {booking.customerEmail || booking.customer?.email || account.billingEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {booking.startDate
                        ? new Date(booking.startDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })
                        : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {booking.startDate
                        ? new Date(booking.startDate).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={booking.status || 'Unknown'}
                      size="small"
                      color={getBookingStatusColor(booking.status)}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {account.currency === 'NGN' ? '₦' : '$'}
                      {booking.totalAmount?.toLocaleString() || '0'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Business color="primary" />
            <Box>
              <Typography variant="h6">{account.companyName}</Typography>
              <Typography variant="caption" color="text.secondary">
                Account ID: {account.id}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Overview" />
          <Tab label="Bookings" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {renderOverviewTab()}
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          {renderBookingsTab()}
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {account.status === 'active' && (
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Block />}
              onClick={() => setSuspendModalOpen(true)}
            >
              Suspend Account
            </Button>
          )}
          {account.status === 'suspended' && (
            <Button
              variant="outlined"
              color="success"
              startIcon={<PlayArrow />}
              onClick={() => setResumeModalOpen(true)}
            >
              Resume Account
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete Account
          </Button>
        </Box>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      {/* Suspend Modal */}
      <SuspendCorporateAccountModal
        open={suspendModalOpen}
        account={account}
        onClose={() => setSuspendModalOpen(false)}
        onSuspend={() => {
          setSuspendModalOpen(false);
          onUpdate?.();
        }}
      />

      {/* Resume Modal */}
      <ResumeCorporateAccountModal
        open={resumeModalOpen}
        account={account}
        onClose={() => setResumeModalOpen(false)}
        onResume={() => {
          setResumeModalOpen(false);
          onUpdate?.();
        }}
      />

      {/* Delete Modal */}
      <DeleteCorporateAccountModal
        open={deleteModalOpen}
        account={account}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={() => {
          setDeleteModalOpen(false);
          onClose(); // Close the details modal after deletion
          onUpdate?.(); // Refresh the list
        }}
      />
    </Dialog>
  );
};
